import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useAssets, type AssetRow } from '../data/fleet.ts'
import { DUE_ORDER, duePillTone, useMeterData, usePlans, useServiceDue } from '../data/meters.ts'
import { useRpc } from '../data/people.ts'
import { ASSET_SECTIONS, lastServiceEvent, machineName, useServiceEvents } from '../data/workshopUi.ts'
import { fmtNum, parseDecimal } from '../lib/format.ts'
import { formatLocal, localDate, noonOfLocalDay } from '../lib/time.ts'
import type { ServiceDueRow } from '../sync/transport.ts'
import { Banner, Field, NotAllowed, PendingBanner, Pill } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { useCloseOnPending } from './workshop/shared.tsx'

type T = (k: string, o?: Record<string, unknown>) => string
/** Why a plan is due (or not), as the list says it. */
function dueReason(r: ServiceDueRow, t: T, lang: string): string[] {
  const out: string[] = []
  if ((r.status === 'due' || r.status === 'overdue') && r.reached_by.length) out.push(t(r.reached_by.length === 2 ? 'service.byBoth' : r.reached_by[0] === 'meter' ? 'service.byMeter' : 'service.byCalendar'))
  if (r.days_remaining !== null && r.status !== 'no_anchor') out.push(r.days_remaining >= 0 ? t('service.daysLeft', { count: r.days_remaining }) : t('service.daysLate', { count: -r.days_remaining }))
  if (r.meter_remaining !== null && r.meter_unit) {
    out.push(r.meter_remaining >= 0 ? t('service.meterLeft', { value: fmtNum(r.meter_remaining, lang), unit: t(`meters.units.${r.meter_unit}`) })
                                     : t('service.meterOver', { value: fmtNum(-r.meter_remaining, lang), unit: t(`meters.units.${r.meter_unit}`) }))
  }
  if (r.status === 'no_anchor') out.push(t('service.noAnchorHelp'))
  if (r.status === 'no_meter') out.push(t('service.noMeterHelp'))
  return out
}

/** The workshop's list (GK-008): what is due, why, and recording that it was done. The server computes it (OR / AND / calendar-only /
 *  meter-only over the lifetime meter value, ignoring suspect readings and pending estimates); this screen only shows the answer.
 *  `?asset=` (M23 item 45, from a machine's page) shows that machine's rows first and alone, with the way back to the whole list. */
export function ServicePage() {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const { asset: focus } = useSearch({ from: '/service' })
  const online = useEngineState().online !== false
  const manage = useWriteAccess('service.manage')
  const record = useWriteAccess('meter.record')
  const permitted = manage.permitted || record.permitted
  const due = useServiceDue()
  const assets = useAssets()
  const [showOk, setShowOk] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [kept, setKept] = useState<string | null>(null)       // the key of the row whose service was kept on the device (M23 item 10)
  const byId = useMemo(() => new Map((assets ?? []).map((a) => [a.id, a])), [assets])
  const tagOf = (id: string) => byId.get(id)?.readable_tag ?? ''
  const rows = useMemo(() => [...(due.data ?? [])].filter((r) => (focus ? r.asset_id === focus : showOk || r.status !== 'ok'))
    .sort((a, b) => DUE_ORDER.indexOf(a.status) - DUE_ORDER.indexOf(b.status) || tagOf(a.asset_id).localeCompare(tagOf(b.asset_id), 'fr', { numeric: true })), [due.data, showOk, focus, byId])   // eslint-disable-line react-hooks/exhaustive-deps
  const first = useRef<HTMLLIElement>(null)
  useEffect(() => { if (focus && rows.length > 0 && typeof first.current?.scrollIntoView === 'function') first.current.scrollIntoView({ block: 'start' }) }, [focus, rows.length])
  if (!permitted) return <NotAllowed permission="service.manage" />

  return (
    <section className="flex flex-col gap-5" data-testid="service-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('service.title')}</h1>
        <Link to="/service/plans" className="btn w-full sm:w-auto" data-testid="go-plans">{t('service.plans')}</Link>
      </div>
      <p className="meta">{t('service.help')}</p>
      {focus && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-brand/40 p-3 dark:border-fairway/40" data-testid="service-focus">
          <span className="font-semibold">{t('service.filterAsset', { tag: tagOf(focus) || '…' })}</span>
          <Link to="/service" className="btn !min-h-11" data-testid="service-all">{t('service.allAssets')}</Link>
        </div>
      )}
      {!online && <Banner tone="warn">{t('service.offline')}</Banner>}
      {due.data === null && online && <p className="opacity-70">{t('service.loading')}</p>}
      {due.data && rows.length === 0 && <p className="opacity-70" data-testid="service-none">{t('service.none')}</p>}
      <ul className="flex flex-col gap-3" data-testid="due-list">
        {rows.map((r, i) => {
          const key = `${r.asset_id}:${r.plan_id}`
          const a = byId.get(r.asset_id)
          return (
            <li key={key} ref={i === 0 ? first : undefined} className={`card flex scroll-mt-20 flex-col gap-2 ${focus ? 'ring-2 ring-brand dark:ring-fairway' : ''}`} data-testid="due-row">
              {/* on a phone the state pill takes its own line under the name (review of M23 item 33: beside it, "TR-02 · Marque Démo FM-5500" broke into three lines) */}
              <Link to="/assets/$assetId" params={{ assetId: r.asset_id }} className="row-link -m-2 flex-wrap !p-2 !shadow-none" data-testid="due-asset">
                <Picto name="mower" className="size-6 shrink-0 text-brand dark:text-fairway" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-lg font-bold">{a?.readable_tag ?? '…'}{machineName(a) ? <span className="font-medium"> · {machineName(a)}</span> : null}</span>
                  <span className="meta">{r.plan_name}</span>
                </span>
                <span className="order-last basis-full sm:order-none sm:basis-auto"><Pill tone={duePillTone(r.status)}>{t(`service.status.${r.status}`)}</Pill></span>
                <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
              </Link>
              <ul className="meta">{dueReason(r, t, i18n.language).map((x, i) => <li key={i}>{x}</li>)}</ul>
              {r.last_service_at && <p className="meta">{t('service.lastService', { when: formatLocal(r.last_service_at, site.timezone, 'dd/MM/yyyy') })}</p>}
              {kept === key && <PendingBanner />}
              {manage.allowed && online && (
                open === key
                  ? <EventForm row={r} onDone={() => { setOpen(null); due.reload() }} onPending={() => { setKept(key); setOpen(null) }} />
                  : <button className="btn !min-h-11" onClick={() => { setKept(null); setOpen(key) }} data-testid="service-done">{t('service.done')}</button>
              )}
            </li>
          )
        })}
      </ul>
      {!focus && due.data && due.data.some((r) => r.status === 'ok') && (
        <label className="flex min-h-14 items-center gap-3 font-medium"><input type="checkbox" className="size-6" checked={showOk} onChange={(e) => setShowOk(e.target.checked)} />{t('service.showOk')}</label>
      )}
      <PendingReadings onDone={due.reload} />
    </section>
  )
}

function EventForm({ row, onDone, onPending }: { row: ServiceDueRow; onDone: () => void; onPending: () => void }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const act = useWorkshopAction()
  const ids = useRef({ event: newId(), obs: newId() })
  const today = localDate(new Date(), site.timezone)
  const [day, setDay] = useState(today)
  const [note, setNote] = useState('')
  const [reading, setReading] = useState('')
  useCloseOnPending(act.pending, onPending)
  return (
    <form className="flex flex-col gap-3 border-t pt-3" data-testid="event-form" onSubmit={(e) => {
      e.preventDefault()
      const n = reading.trim() ? parseDecimal(reading) : null
      if (reading.trim() && n === null) return
      void act.run('service_event.record@1', {
        event_id: ids.current.event, asset_id: row.asset_id, plan_id: row.plan_id,
        ...(day !== today ? { performed_at: noonOfLocalDay(day, site.timezone) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(n !== null && row.meter_slot_id ? { meter_value: n, observation_id: ids.current.obs } : {}),
      }, 'service.doneOk').then((ok) => { if (ok) onDone() })
    }}>
      <Field label={t('service.doneWhen')} htmlFor={`d-${row.plan_id}-${row.asset_id}`}>
        <input id={`d-${row.plan_id}-${row.asset_id}`} className="input" type="date" max={today} value={day} onChange={(e) => setDay(e.target.value)} data-testid="event-date" />
      </Field>
      {row.meter_slot_id && (
        <Field label={t('service.doneReading')} htmlFor={`r-${row.plan_id}-${row.asset_id}`}>
          <input id={`r-${row.plan_id}-${row.asset_id}`} className="input" inputMode="decimal" value={reading} onChange={(e) => setReading(e.target.value)} data-testid="event-reading" />
        </Field>
      )}
      <Field label={t('service.doneNote')} htmlFor={`n-${row.plan_id}-${row.asset_id}`}><input id={`n-${row.plan_id}-${row.asset_id}`} className="input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="event-note" /></Field>
      {act.error && <Banner tone="error">{act.error}</Banner>}
      <button className="btn btn-primary" disabled={act.busy || !day} data-testid="event-go">{t('service.doneGo')}</button>
    </form>
  )
}

/** Readings that do not count yet (a suspect one, an estimate, one from a device that had not heard of a counter replacement). */
function PendingReadings({ onDone }: { onDone: () => void }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const online = useEngineState().online !== false
  const manage = useWriteAccess('meter.manage')
  const assets = useAssets()
  const { slots, instances, observations } = useMeterData()
  const act = useWorkshopAction()
  const pending = useMemo(() => (observations ?? []).filter((o) => o.review_state === 'pending' && !o.superseded_by_id), [observations])
  const rows = useMemo(() => pending.map((o) => {
    const inst = (instances ?? []).find((i) => i.id === o.meter_instance_id)
    const slot = (slots ?? []).find((s) => s.id === inst?.slot_id)
    return { o, slot, tag: (assets ?? []).find((a) => a.id === slot?.asset_id)?.readable_tag ?? '…' }
  }), [pending, instances, slots, assets])
  if (!observations || pending.length === 0) return null
  return (
    <section className="flex flex-col gap-3" data-testid="pending-readings">
      <h2 className="text-xl font-bold">{t('meters.reviewTitle')}</h2>
      <p className="meta">{t('meters.reviewHelp')}</p>
      {act.error && <Banner tone="error">{act.error}</Banner>}
      {act.pending && <PendingBanner />}
      <ul className="flex flex-col gap-2">
        {rows.map(({ o, slot, tag }) => (
          <li key={o.id} className="card flex flex-col gap-2" data-testid="pending-row">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-lg font-bold">{tag} · {fmtNum(o.value, i18n.language)} {slot ? t(`meters.units.${slot.unit_code}`) : ''}</span>
              <span className="flex flex-wrap gap-1">
                {o.source === 'estimated' && <Pill tone="gray">{t('meters.pill.estimate')}</Pill>}
                {o.quality !== 'ok' && <Pill tone="red">{t(`meters.quality.${o.quality}`)}</Pill>}
                {o.quality === 'ok' && o.source !== 'estimated' && <Pill tone="amber">{t('meters.pill.toReview')}</Pill>}
              </span>
            </div>
            <p className="meta">{formatLocal(o.observed_at, site.timezone, 'dd/MM/yyyy HH:mm')}{o.estimate_method ? ` · ${o.estimate_method}` : ''}</p>
            {manage.allowed && online && (
              <div className="grid grid-cols-2 gap-2">
                {(['reject', 'accept'] as const).map((d) => (
                  <button key={d} className={`btn !min-h-11 ${d === 'accept' ? 'btn-primary' : ''}`} disabled={act.busy} data-testid={`pending-${d}`}
                    onClick={() => void act.run('meter.review@1', { observation_id: o.id, decision: d }, 'meters.reviewed').then(onDone)}>{t(`meters.${d}`)}</button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** A machine's own service (M23 item 45, on its page): the last service recorded, from the device's mirror (so it is there offline), and what
 *  the server says is due for THIS machine when online (`useRpc(...).fresh`: never another machine's answer; nothing is computed here, ADR-060).
 *  Each row opens the service list on that machine. Offline: a dash and the reason. */
export function AssetServiceSection({ asset }: { asset: Pick<AssetRow, 'id'> }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const events = useServiceEvents()
  const plans = usePlans()
  const q = useRpc<ServiceDueRow[]>('service_due', { p_asset: asset.id })
  const last = lastServiceEvent(events, asset.id)
  const planName = (id: string) => plans?.find((p) => p.id === id)?.name ?? '…'
  const rows = q.data && q.fresh ? q.data.filter((r) => r.asset_id === asset.id) : null
  return (
    <section id={ASSET_SECTIONS.service} className="flex scroll-mt-20 flex-col gap-3" data-testid="asset-service">
      <h2 className="text-xl font-bold">{t('assets.serviceSection')}</h2>
      {last
        ? <p className="meta" data-testid="asset-service-last">{t('assets.serviceLast', { name: planName(last.plan_id), when: formatLocal(last.performed_at, site.timezone, 'dd/MM/yyyy') })}</p>
        : <p className="meta" data-testid="asset-service-never">{t('assets.serviceNever')}</p>}
      {q.online === false ? <p className="meta" data-testid="asset-service-offline">— · {t('assets.serviceOffline')}</p>
        : q.error ? null
        : rows === null ? <p className="meta">{t('service.loading')}</p>
        : rows.length === 0 ? <p className="meta" data-testid="asset-service-none">{t('assets.serviceNone')}</p>
        : (
          <ul className="flex flex-col gap-2" data-testid="asset-service-list">
            {rows.map((r) => (
              <li key={r.plan_id}>
                {/* the words first, the state pill after them (review of item 45: the pill first left the plan's name a 110-px column at 375, one word per line) */}
                <Link to="/service" search={{ asset: asset.id }} className="row-link flex-wrap" data-testid="asset-service-row" data-status={r.status}>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="font-semibold">{r.plan_name}</span>
                    <span className="meta">{dueReason(r, t, i18n.language).join(' · ')}</span>
                  </span>
                  <span className="order-last basis-full sm:order-none sm:basis-auto"><Pill tone={duePillTone(r.status)}>{t(`service.status.${r.status}`)}</Pill></span>
                  <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
                </Link>
              </li>
            ))}
          </ul>
        )}
    </section>
  )
}
